import { useEffect, useRef, useState } from 'react'
import type { DatenschutzHeader, DatenschutzSelections } from '@/core/data/portalViews'
import type { DatenschutzConsent } from '@/core/api/portal'
import { Checkbox } from '@/core/components/form/Checkbox'
import { PdfRegion, ZOOMED_OUT_PDF_SCALE } from '@/core/components/modal/PdfRegion'
import { LoadingState, ScrollBar, toastError } from 'admination-react'
import { downloadBlob } from '@/core/lib/files'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { useListActionsSink } from '@/core/components/list/listActions'

export function DatenschutzTab({
  header,
  selections,
  onChange,
  signed,
  pdfPath,
  onFetchInfo,
  onFetchConsent,
  onGenerate,
  onSign,
  onDownloadSigned,
}: {
  header: DatenschutzHeader
  selections: DatenschutzSelections
  onChange: (key: keyof DatenschutzSelections, value: boolean) => void
  signed: boolean
  pdfPath: string | null

  onFetchInfo: () => Promise<Blob | null>

  onFetchConsent: () => Promise<DatenschutzConsent | null>

  onGenerate: (selections: DatenschutzSelections) => Promise<void>

  onSign: () => void

  onDownloadSigned: () => void
}) {

  const [step, setStep] = useState<1 | 2>(1)

  const scrollerRef = useRef<HTMLElement>(null)

  const [editing, setEditing] = useState(false)
  const [pending, setPending] = useState(false)

  const [infoBlob, setInfoBlob] = useState<Blob | null>(null)
  const infoPromiseRef = useRef<Promise<Blob | null> | null>(null)
  function fetchInfoOnce(): Promise<Blob | null> {
    if (!infoPromiseRef.current) {
      infoPromiseRef.current = onFetchInfo()
        .then((blob) => {
          setInfoBlob(blob)
          return blob
        })
        .catch((e: unknown) => {
          toastError(
            friendlyErrorMessage(e, "Informationsblatt konnte nicht geladen werden. Bitte versuchen Sie es erneut."),
          )
          return null
        })
    }
    return infoPromiseRef.current
  }

  const [consent, setConsent] = useState<DatenschutzConsent | null>(null)
  const [consentError, setConsentError] = useState(false)
  const initialConsentLoader = useRef(onFetchConsent)
  useEffect(() => {
    let cancelled = false
    initialConsentLoader.current()
      .then((result) => {
        if (cancelled) return
        if (!result) {
          setConsentError(true)
          return
        }
        setConsent(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        toastError(friendlyErrorMessage(e, "Einwilligungstext konnte nicht geladen werden. Bitte versuchen Sie es erneut."))
        setConsentError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function handleGenerate() {
    if (pending) return
    setPending(true)
    try {
      await onGenerate(selections)
      setEditing(false)
    } catch {
      return
    } finally {
      setPending(false)
    }
  }

  function handleEditSelections() {
    setEditing(true)
    setStep(2)
  }

  const sink = useListActionsSink()
  const callbacks = useRef({ handleGenerate, onDownloadSigned, onSign })
  callbacks.current = { handleGenerate, onDownloadSigned, onSign }
  useEffect(() => {
    const { handleGenerate, onDownloadSigned, onSign } = callbacks.current
    if (!sink) return
    if (signed) {
      sink({
        actions: [
          { id: 'download', label: "Herunterladen", icon: 'i-download', primary: true, iconOnly: true },
        ],
        onRun: (id) => {
          if (id === 'download') onDownloadSigned()
        },
      })
      return () => sink(null)
    }
    if (pdfPath && !editing) {
      sink({
        actions: [
          { id: 'edit', label: "Auswahl ändern", icon: 'i-edit', iconOnly: true },
          { id: 'sign', label: "Unterschreiben", icon: 'i-signature', primary: true },
        ],
        onRun: (id) => {
          if (id === 'edit') handleEditSelections()
          else if (id === 'sign') onSign()
        },
      })
      return () => sink(null)
    }
    if (step === 1) {
      sink({
        actions: [
          { id: 'download', label: "Herunterladen", icon: 'i-download', disabled: !infoBlob, iconOnly: true },
          { id: 'next', label: "Weiter", icon: 'i-arrow-right', primary: true, iconOnly: true },
        ],
        onRun: (id) => {
          if (id === 'download') downloadBlob(infoBlob, "informationsblatt.pdf")
          else if (id === 'next') setStep(2)
        },
      })
      return () => sink(null)
    }
    sink({
      actions: [
        { id: 'back', label: "Zurück", icon: 'i-arrow-left', iconOnly: true, disabled: pending },
        {
          id: 'next',
          label: "Weiter",
          icon: 'i-arrow-right',
          primary: true,
          loading: pending,
          iconOnly: true,
        },
      ],
      onRun: (id) => {
        if (id === 'back') setStep(1)
        else if (id === 'next') void handleGenerate()
      },
    })
    return () => sink(null)
  }, [sink, signed, pdfPath, editing, step, infoBlob, pending, selections])

  if (pending) return <PdfRegion path={null} waiting loadingLabel="Datenschutzerklärung wird erstellt …" placeholder="" />

  if (signed || (pdfPath && !editing)) {
    const label = signed ? "Datenschutzerklärung" : "Einwilligung"
    return <PdfRegion path={pdfPath} loadingLabel={`${label} wird geladen …`}
      placeholder={`${label} wird geladen…`} scaleFactor={ZOOMED_OUT_PDF_SCALE} />
  }

  if (step === 1) {
    return <PdfRegion path="datenschutz-info" loadingLabel="Informationsblatt wird geladen …"
      placeholder="Das Informationsblatt fehlt. Bitte wenden Sie sich an die Organisationsverwaltung."
      download={() => fetchInfoOnce()} scaleFactor={ZOOMED_OUT_PDF_SCALE} />
  }

  return (
    <>
    <main className="form" data-scroll-host ref={scrollerRef}>
      <div className="form-sheet consent-prose">
        {consentError ? (
          <p className="section-note">
            Der Einwilligungstext konnte nicht geladen werden. Bitte versuchen Sie es erneut.</p>
        ) : !consent ? (
          <LoadingState>Der Einwilligungstext wird geladen …</LoadingState>
        ) : (
          <>

            <p>
              {consent.prose.hiermit_willige} {header.vorname} {header.nachname},{' '}
              {consent.prose.ein_dass}
            </p>
            <p>
              {consent.traeger.name}
              <br />
              {consent.traeger.strasse} {consent.traeger.hausnummer}
              <br />
              {consent.traeger.plz} {consent.traeger.ort},
            </p>
            <p>{consent.prose.zweck_lead}</p>
            <div className="consent-options">
              {consent.einwilligungen.map((item) => (
                <Checkbox
                  key={item.key}
                  label={item.label}
                  checked={selections[item.key as keyof DatenschutzSelections]}
                  onChange={(v) => onChange(item.key as keyof DatenschutzSelections, v)}
                />
              ))}
            </div>
            <p className="consent-withdrawal">{consent.prose.widerruf}</p>
            <p>{consent.prose.informationen_erhalten}</p>
          </>
        )}
      </div>
    </main>
    <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
